import tokenService from 'services/tokenService';
import decode from 'jwt-decode';
import store from 'redux/store';
import { authActions, authSelectors } from 'redux/auth';
import { snackbarActions } from 'redux/snackbar';
import { abortSignalTimeout, isFailedToFetchError } from 'redux/helpers/utils';
import history from 'providers/history';
import { isPassportEnabled } from 'helpers';
import legacyAuthProvider from './authProviders/legacyAuthProvider';
import passportAuthProvider from './authProviders/passportAuthProvider';

let tokenRefreshPromise = null;
let forcedRefreshPromise = null;

const authProvider = isPassportEnabled() ? passportAuthProvider : legacyAuthProvider;

const getTokenExpiration = (token) => {
  if (!token) {
    return null;
  }

  try {
    return decode(token).exp;
  } catch (e) {
    return null;
  }
};

const isTokenExpiringSoon = (token, ms = 30 * 1000) => {
  const expiration = getTokenExpiration(token);
  const expirationMs = expiration * 1000;
  const now = Date.now();

  return expirationMs <= now + ms;
};

const refreshTokenFail = async (error, { silent = false } = {}) => {
  if (!isFailedToFetchError(error)) {
    await tokenService.clearSession(); // TODO: Temporarily disabled

    store.dispatch(authActions.refreshTokenFailure(error));
  }

  if (!silent) {
    // Stable key + preventDuplicate so a burst of parallel refresh failures collapses to one toast.
    store.dispatch(snackbarActions.createFailure(error.message, {
      translate: false,
      key: 'auth-refresh-failure',
      preventDuplicate: true,
    }));
  }

  return { ok: false, error, data: null, status: 401 };
};


// A refresh only renews the token. A session it starts, as after an OAuth callback, still has to load the user
const refreshTokenSuccess = async () => {

  store.dispatch(authActions.refreshTokenSuccess());

  if (!authSelectors.getUserIsLoggedIn(store.getState())) {
    store.dispatch(authActions.getAuthUser());
  }

  return { ok: true, response: null, error: null };
};

const renewToken = async () => {
  const isRefreshable = await authProvider.isRefreshable();
  if (!isRefreshable) {
    return refreshTokenFail({ message: 'Member is not logged in' });
  }

  const result = await authProvider.fetchNewToken()
    .then(() => refreshTokenSuccess())
    .catch(refreshTokenFail);

  return result;
};

// No smart logic.
// Navigator lock here simply to avoid parallel refresh requests.
// Either to trigger refresh or not should be done elsewhere
const refreshToken = async () => {
  return navigator.locks.request('guava-refreshToken', renewToken);
};

export const forceRefreshToken = (rejectedToken) => {
  forcedRefreshPromise ??= navigator.locks.request('guava-refreshToken', async () => {
    const tokenWasReplaced = await tokenService.getAccessToken() !== rejectedToken;

    return tokenWasReplaced ? { ok: true } : renewToken();
  }).finally(() => {
    forcedRefreshPromise = null;
  });

  return forcedRefreshPromise;
};

export const handleOauthCallback = async (code) => {
  return passportAuthProvider.exchangeCodeForTokens(code)
    .then(() => refreshTokenSuccess())
    .then(() => history.push('/'));
};

export const onAppInit = async ({ location } = {}) => {

  if (await passportAuthProvider.isPassportUrl(location)) {
    await passportAuthProvider.handlePassportUrl(location)
      .catch(e => refreshTokenFail(e));
  }

  const token = await tokenService.getAccessToken();

  if (!token) {
    return refreshTokenFail({ message: 'Member is not logged in' }, { silent: true });
  }

  await ensureValidToken()
    .then(() => store.dispatch(authActions.getAuthUser()))
    .catch(() => refreshTokenFail({ message: 'Member is not logged in' }));
};

// TTL (ms). Refresh token before expiration.
// TimeoutTTL (ms) - Refresh query timeout.
export const ensureValidToken = async (ttl = 30 * 1000, timeoutTtl = 30 * 1000) => {
  const accessToken = await tokenService.getAccessToken();
  if (!accessToken) {
    return refreshTokenFail({ message: 'Member is not logged in' });
  }

  const shouldRefreshToken = isTokenExpiringSoon(accessToken, ttl);
  if (!shouldRefreshToken) {
    return;
  }

  // Same tab doing multiple requests
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  // Prevent multiple tabs doing the same thing
  tokenRefreshPromise = navigator.locks.request('guava-ensureValidToken', { signal: abortSignalTimeout(timeoutTtl) }, async () => {

    // Check for valid token. Lock might have been just released...
    const $accessToken = await tokenService.getAccessToken();
    const $shouldRefreshToken = isTokenExpiringSoon($accessToken, ttl);
    if (!$shouldRefreshToken) {
      return;
    }

    await refreshToken();
  })
    .catch((e) => {
      tokenRefreshPromise = null;

      const errorMessage = e.name === 'AbortError'
        ? 'Token refresh timed out!'
        : e.message || e.name || 'Unknown error';

      return refreshTokenFail({ message: errorMessage });
    })
    .finally(() => {
      tokenRefreshPromise = null;
    });

  return tokenRefreshPromise;
};
